import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import { useTheme } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import Collapse from '@mui/material/Collapse';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';

import { RouterLink } from 'routes/components';

import { varAlpha } from 'theme/styles';

import { Logo } from 'components/logo';
import { Scrollbar } from 'components/scrollbar';

import { hasPermission, hasRole } from 'components/authorization/PermissionUtils';
import { getUserEntity } from 'common/UserEntityProvider';
import { ENTITY_TYPE } from 'common/constants';
import intl from 'components/i18n/ReactIntlWrapper';
import { Divider } from '@mui/material';
import { useLocation } from 'react-router';

// ----------------------------------------------------------------------

export type MenuItemDef = {
  path?: string;
  labelKey: string;
  icon?: React.ReactNode;
  info?: React.ReactNode;
  viewScopes?: string[];
  editScopes?: string[];
  viewRoles?: string[];
  editRoles?: string[];
  entityType?: number;
  children?: MenuItemDef[];
};

export type NavContentProps = {
  data: MenuItemDef[];
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  sx?: SxProps<Theme>;
};

export function NavDesktop({
  sx,
  data,
  slots,
  layoutQuery,
}: NavContentProps & { layoutQuery: Breakpoint }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 0,
        top: 0,
        left: 0,
        height: 1,
        display: 'none',
        position: 'fixed',
        flexDirection: 'column',
        bgcolor: 'var(--layout-nav-bg)',
        zIndex: 'var(--layout-nav-zIndex)',
        width: 'var(--layout-nav-vertical-width)',
        borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
        boxShadow: '0px 0px 10px lightgrey',
        [theme.breakpoints.up(layoutQuery)]: {
          display: 'flex',
        },
        ...sx,
      }}
    >
      <NavContent data={data} slots={slots} />
    </Box>
  );
}

// ----------------------------------------------------------------------

export function NavMobile({
  sx,
  data,
  open,
  slots,
  onClose,
}: NavContentProps & { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation();

  useEffect(() => {
    if (open) {
      onClose();
    }
  }, [pathname]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          p: 0,
          overflow: 'unset',
          bgcolor: 'var(--layout-nav-bg)',
          width: 'var(--layout-nav-mobile-width)',
          ...sx,
        },
      }}
    >
      <NavContent data={data} slots={slots} />
    </Drawer>
  );
}

// ----------------------------------------------------------------------

export function NavContent({ data, slots, sx }: NavContentProps) {
  const { pathname } = useLocation();

  return (
    <>
      <Logo />

      {slots?.topArea}
      <Divider />
      <Scrollbar fillContent>
        <Box
          component="nav"
          display="flex"
          flex="1 1 auto"
          flexDirection="column"
          sx={{
            ...sx,
          }}
        >
          <Box component="ul" gap={0.5} display="flex" flexDirection="column">
            {data.map((item, index) => (
              <NavItem key={`${item.labelKey}-${index}`} item={item} pathname={pathname} />
            ))}
          </Box>
        </Box>
      </Scrollbar>

      {slots?.bottomArea}
    </>
  );
}

// New component to handle both parent and child menu items
function NavItem({
  item,
  pathname,
  level = 0,
}: {
  item: MenuItemDef;
  pathname: string;
  level?: number;
}) {
  const [open, setOpen] = useState(false);
  const isActived = item.path === pathname;
  const hasChildren = item.children && item.children.length > 0;
  const userEntity = getUserEntity();
  const viewScopes = item.viewScopes || [];
  const editScopes = item.editScopes || [];
  const viewRoles = item.viewRoles || [];
  const editRoles = item.editRoles || [];
  const entityType = item.entityType || ENTITY_TYPE.ANY;

  // Check if any child is active to highlight parent
  const isChildActive =
    hasChildren &&
    item.children?.some(
      (child) =>
        child.path === pathname ||
        (child.children && child.children.some((subChild) => subChild.path === pathname))
    );

  // Auto-expand menu if a child is active
  useEffect(() => {
    if (isChildActive) {
      setOpen(true);
    }
  }, [isChildActive]);

  const handleClick = () => {
    if (hasChildren) {
      setOpen(!open);
    }
  };

  function checkPermission(scopes, roles, entityType) {
    return (
      (hasPermission({ scopes }) || hasRole({ userRoles: [], roles }) || scopes.length === 0) &&
      (entityType === userEntity?.entityType || entityType === ENTITY_TYPE.ANY)
    );
  }

  if (!checkPermission(viewScopes, viewRoles, entityType)) {
    return null;
  }

  const resolveLabelKey = (item: MenuItemDef): string => {
    // console.log('RESOLVE RESOLVE', item, ENTITY_TYPE);
    if (item.labelKey === 'mainNavigation.invoiceOrBilling') {
      if (
        userEntity?.entityType === ENTITY_TYPE.EMPLOYEE ||
        userEntity?.entityType === ENTITY_TYPE.PROVIDER
      ) {
        return intl.get('mainNavigation.invoice');
      } else if (
        userEntity?.entityType === ENTITY_TYPE.CORPORATE_ENTITY ||
        userEntity?.entityType === ENTITY_TYPE.CLIENT
      ) {
        return intl.get('mainNavigation.billing');
      }
    }
    return intl.get(item.labelKey);
  };

  return (
    <>
      <ListItem disableGutters disablePadding>
        <ListItemButton
          disableGutters
          onClick={handleClick}
          component={hasChildren ? 'div' : RouterLink}
          href={!hasChildren ? item.path : undefined}
          sx={{
            pl: level ? level + 3 : 3,
            py: 1,
            gap: 2,
            pr: 1.5,
            borderRadius: 0.75,
            typography: 'body2',
            fontWeight: 'fontWeightMedium',
            color: 'var(--layout-nav-item-color)',
            minHeight: 'var(--layout-nav-item-height)',
            ...(isActived && {
              fontWeight: 'fontWeightSemiBold',
              bgcolor: 'var(--layout-nav-item-active-bg)',
              color: 'var(--layout-nav-item-active-color)',
              '&:hover': {
                bgcolor: 'var(--layout-nav-item-hover-bg)',
              },
            }),
            ...(isChildActive && {
              fontWeight: 'fontWeightSemiBold',
            }),
          }}
        >
          <Box component="span" sx={{ width: 24, height: 24 }}>
            {item.icon}
          </Box>

          <Box component="span" flexGrow={1}>
            {resolveLabelKey(item)}
          </Box>

          {item.info && item.info}

          {hasChildren && (open ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      </ListItem>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box component="ul" sx={{ pl: level ? 0 : 1 }}>
            {item.children?.map((child, index) => (
              <NavItem
                key={`${index}-${item.labelKey}-${child.labelKey}`}
                item={child}
                pathname={pathname}
                level={level + 1}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </>
  );
}

NavItem.displayName = 'NavItem';
